import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { PRIORITY_CONFIG, TYPE_CONFIG } from '@config/constants';
import { formatDay } from '@utils/calendarDate';

// Read-only board for the client portal: one column per status the team
// marked as "visible to the client", listing the tasks currently in it.
const styles = {
  card: {
    background: 'rgba(255, 255, 255, 0.72)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15), inset 0 0 0 1px rgba(255, 255, 255, 0.3)',
    padding: '24px',
    marginTop: '24px',
  },
  title: { fontSize: '17px', fontWeight: '600', color: '#000000', margin: 0 },
  subtitle: { fontSize: '12px', color: '#3c3c4399', margin: '2px 0 0 0' },
  columns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '16px',
    marginTop: '24px',
  },
  column: {
    background: '#f2f2f7',
    borderRadius: '16px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: 0,
  },
  task: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
  },
  empty: { fontSize: '13px', color: '#3c3c4399', textAlign: 'center', padding: '16px 0' },
};

const formatDate = (value) => formatDay(value, { day: 'numeric', month: 'short' });

export default function ClientVisibleTasks({ statuses, tasks }) {
  const columns = useMemo(
    () =>
      statuses.map((status) => ({
        ...status,
        tasks: tasks.filter((t) => t.status === status.name),
      })),
    [statuses, tasks]
  );

  if (statuses.length === 0) return null;

  return (
    <section style={styles.card}>
      <h3 style={styles.title}>Tareas</h3>
      <p style={styles.subtitle}>Estado actual del trabajo compartido contigo</p>

      <div style={styles.columns}>
        {columns.map((column) => (
          <div key={column.id} style={styles.column}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: column.color }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: column.color }}>{column.label}</span>
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#3c3c4399' }}>
                {column.tasks.length}
              </span>
            </div>

            {column.tasks.map((task) => {
              const type = TYPE_CONFIG[task.type?.toLowerCase()];
              const priority = PRIORITY_CONFIG[task.priority?.toLowerCase()];
              return (
                <div key={task.id} style={styles.task}>
                  <p style={{ fontSize: '14px', fontWeight: '500', color: '#000000', margin: 0, wordBreak: 'break-word' }}>
                    {task.title}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px', fontSize: '12px' }}>
                    {type && <span style={{ color: type.color }}>{type.icon} {type.label}</span>}
                    {priority && <span style={{ color: priority.color }}>{priority.label}</span>}
                    {task.dueDate && (
                      <span style={{ color: '#3c3c43' }}>Entrega: {formatDate(task.dueDate)}</span>
                    )}
                  </div>
                </div>
              );
            })}

            {column.tasks.length === 0 && <p style={styles.empty}>Sin tareas</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

ClientVisibleTasks.propTypes = {
  statuses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  tasks: PropTypes.array.isRequired,
};
